/** Shared pull-to-refresh controls for feature scrollers and the mobile app shell. */
import React, { useState, useEffect, useCallback, useRef, type RefObject } from 'react';
import { Loader2, ArrowDown, RefreshCw } from '@/shared/ui/icons';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export function PullToRefresh({
  onRefresh,
  children,
  className = '',
  disabled = false,
}: PullToRefreshProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const pullDistanceRef = useRef(0);
  const isPullingRef = useRef(false);

  const PULL_THRESHOLD = 80;
  const MAX_PULL = 120;

  // Keep the 60-120Hz gesture off React's render path. Only the semantic
  // refreshing state causes a render; live distance is a compositor transform
  // plus two small style writes.
  const paintDistance = (distance: number, animate: boolean) => {
    pullDistanceRef.current = distance;
    const transition = animate
      ? 'transform 0.28s cubic-bezier(0.16, 1, 0.3, 1)'
      : 'none';
    if (contentRef.current) {
      contentRef.current.style.transform = `translateY(${distance}px)`;
      contentRef.current.style.transition = transition;
    }
    if (indicatorRef.current) {
      indicatorRef.current.style.height = `${distance}px`;
      indicatorRef.current.style.opacity = distance > 20 ? '1' : '0';
      indicatorRef.current.style.transition = animate
        ? 'height 0.28s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.18s ease'
        : 'none';
    }
    if (arrowRef.current) {
      arrowRef.current.style.transform = `rotate(${distance >= PULL_THRESHOLD ? 180 : 0}deg)`;
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (disabled || isRefreshing) return;
    
    // Only allow pull to refresh if we are at the top of the container
    const container = containerRef.current;
    if (container && container.scrollTop > 0) return;
    
    startY.current = e.touches[0].pageY;
    isPullingRef.current = true;
    paintDistance(pullDistanceRef.current, false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isPullingRef.current || isRefreshing) return;

    const currentY = e.touches[0].pageY;
    const distance = currentY - startY.current;

    if (distance > 0) {
      // Resistance effect
      const dampedDistance = Math.min(distance * 0.4, MAX_PULL);
      paintDistance(dampedDistance, false);
      
      // If we've pulled enough, prevent default to avoid native bounce
      if (dampedDistance > 10) {
        if (e.cancelable) e.preventDefault();
      }
    } else {
      paintDistance(0, true);
      isPullingRef.current = false;
    }
  };

  const handleTouchEnd = async () => {
    if (!isPullingRef.current || isRefreshing) return;
    isPullingRef.current = false;

    if (pullDistanceRef.current >= PULL_THRESHOLD) {
      setIsRefreshing(true);
      paintDistance(PULL_THRESHOLD, true);
      
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        paintDistance(0, true);
      }
    } else {
      paintDistance(0, true);
    }
  };

  const handleTouchCancel = () => {
    if (isRefreshing) return;
    isPullingRef.current = false;
    paintDistance(0, true);
  };

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
    >
      {/* Pull Indicator */}
      <div
        ref={indicatorRef}
        className="absolute top-0 left-0 right-0 flex justify-center items-center pointer-events-none z-50"
        style={{
          height: pullDistanceRef.current,
          opacity: pullDistanceRef.current > 20 ? 1 : 0,
          transition: isRefreshing
            ? 'height 0.28s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.18s ease'
            : undefined,
        }}
      >
        <div className="flex flex-col items-center">
          {isRefreshing ? (
            <Loader2 className="w-6 h-6 animate-spin text-brand" />
          ) : (
            <div
              ref={arrowRef}
              className="bg-glass-bg-strong p-2 rounded-full shadow-elevation-sm border border-glass-border"
              style={{ transition: 'transform 120ms ease' }}
            >
              <ArrowDown className="w-4 h-4 text-brand" />
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div
        ref={contentRef}
        className="h-full"
        style={{
          transform: `translateY(${pullDistanceRef.current}px)`,
          transition: isRefreshing
            ? 'transform 0.28s cubic-bezier(0.16, 1, 0.3, 1)'
            : undefined,
        }}
      >
        {children}
      </div>
    </div>
  );
}

interface ShellPullToRefreshProps {
  scrollContainerRef: RefObject<HTMLElement | null>;
  onRefresh: () => Promise<void>;
  disabled?: boolean;
  enableMouseGesture?: boolean;
}

const SHELL_PULL_THRESHOLD = 88;
const SHELL_MAX_PULL = 124;
const SHELL_REFRESH_DISTANCE = 88;
const SHELL_REFRESH_MINIMUM_MS = 550;

const NESTED_SCROLL_SELECTOR = [
  '[data-radix-scroll-area-viewport]',
  '[data-slot="scroll-area-viewport"]',
  '.overflow-y-auto',
  '.overflow-y-scroll',
  '.overflow-auto',
  '.overflow-scroll',
  '[data-pull-to-refresh-disabled="true"]',
].join(',');

type ShellPullState = 'idle' | 'pulling' | 'ready' | 'refreshing';

export function ShellPullToRefresh({
  scrollContainerRef,
  onRefresh,
  disabled = false,
  enableMouseGesture = false,
}: ShellPullToRefreshProps) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const pullSurfaceRef = useRef<HTMLElement | null>(null);
  const iconRef = useRef<HTMLSpanElement>(null);
  const statusRef = useRef<HTMLSpanElement>(null);
  const snapCleanupRef = useRef<(() => void) | null>(null);
  const gestureRef = useRef({
    active: false,
    refreshing: false,
    ready: false,
    startX: 0,
    startY: 0,
    distance: 0,
    pointerId: null as number | null,
  });
  const frameRef = useRef<number | null>(null);
  const pendingDistanceRef = useRef(0);

  const setVisualState = useCallback((state: ShellPullState) => {
    const backdrop = backdropRef.current;
    if (!backdrop) return;
    backdrop.dataset.state = state;
    backdrop.setAttribute(
      'aria-label',
      state === 'ready' || state === 'refreshing'
        ? 'Refreshing'
        : 'Pull to refresh'
    );
    if (pullSurfaceRef.current) pullSurfaceRef.current.dataset.pullRefreshState = state;
    if (statusRef.current) {
      statusRef.current.textContent =
        state === 'ready' || state === 'refreshing'
          ? 'Refreshing'
          : 'Pull to refresh';
    }
  }, []);

  const paintDistance = useCallback((distance: number) => {
    const backdrop = backdropRef.current;
    const pullSurface = pullSurfaceRef.current;
    if (!backdrop || !pullSurface) return;
    const progress = Math.min(1, distance / SHELL_PULL_THRESHOLD);
    pullSurface.style.setProperty('--qs-pull-refresh-offset', `${distance}px`);
    if (distance > 0) backdrop.style.opacity = '1';
    if (iconRef.current && !gestureRef.current.refreshing) {
      iconRef.current.style.transform = `rotate(${progress * 270}deg)`;
    }
  }, []);

  const schedulePaint = useCallback((distance: number) => {
    pendingDistanceRef.current = distance;
    if (frameRef.current !== null) return;
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null;
      paintDistance(pendingDistanceRef.current);
    });
  }, [paintDistance]);

  const resetIndicator = useCallback(() => {
    snapCleanupRef.current?.();
    const gesture = gestureRef.current;
    gesture.active = false;
    gesture.ready = false;
    gesture.distance = 0;
    setVisualState('idle');
    const pullSurface = pullSurfaceRef.current;
    if (!pullSurface) {
      backdropRef.current?.style.setProperty('opacity', '0');
      return;
    }

    let firstFrame: number | null = null;
    let secondFrame: number | null = null;
    const onTransitionFinished = (event: TransitionEvent) => {
      if (event.target === pullSurface && event.propertyName === 'transform') finishSnap();
    };
    const cancelSnap = () => {
      pullSurface.removeEventListener('transitionend', onTransitionFinished);
      pullSurface.removeEventListener('transitioncancel', onTransitionFinished);
      if (firstFrame !== null) window.cancelAnimationFrame(firstFrame);
      if (secondFrame !== null) window.cancelAnimationFrame(secondFrame);
      if (snapCleanupRef.current === cancelSnap) snapCleanupRef.current = null;
    };
    const finishSnap = () => {
      cancelSnap();
      backdropRef.current?.style.setProperty('opacity', '0');
      pullSurface.style.removeProperty('--qs-pull-refresh-offset');
      delete pullSurface.dataset.pullRefreshState;
    };

    pullSurface.addEventListener('transitionend', onTransitionFinished);
    pullSurface.addEventListener('transitioncancel', onTransitionFinished);
    snapCleanupRef.current = cancelSnap;
    schedulePaint(0);
    // A zero-distance reset and reduced-motion mode do not emit transitionend.
    // After the style has painted, clean up immediately only when the browser
    // confirms there is no active transition to wait for.
    firstFrame = window.requestAnimationFrame(() => {
      firstFrame = null;
      secondFrame = window.requestAnimationFrame(() => {
        secondFrame = null;
        const animations = pullSurface.getAnimations?.() ?? [];
        if (!animations.some((animation) => animation.playState === 'running')) {
          finishSnap();
        }
      });
    });
  }, [schedulePaint, setVisualState]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || disabled) {
      resetIndicator();
      return;
    }
    pullSurfaceRef.current = Array.from(container.children).find(
      (element): element is HTMLElement =>
        element instanceof HTMLElement && element.classList.contains('qs-shell-content-inner')
    ) ?? null;

    const gesture = gestureRef.current;

    const canStartGesture = (target: EventTarget | null) => {
      if (
        gesture.refreshing ||
        container.scrollTop > 0 ||
        document.documentElement.dataset.keyboardOpen === '1'
      ) return false;

      if (target instanceof Element) {
        const nestedScroller = target.closest(NESTED_SCROLL_SELECTOR);
        if (nestedScroller && nestedScroller !== container) return false;
      }
      return true;
    };

    const beginGesture = (clientX: number, clientY: number) => {
      snapCleanupRef.current?.();
      backdropRef.current?.style.setProperty('opacity', '1');
      setVisualState('pulling');
      gesture.active = true;
      gesture.ready = false;
      gesture.distance = 0;
      gesture.startX = clientX;
      gesture.startY = clientY;
    };

    const moveGesture = (clientX: number, clientY: number) => {
      if (!gesture.active || gesture.refreshing) return false;

      const deltaX = clientX - gesture.startX;
      const deltaY = clientY - gesture.startY;
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        resetIndicator();
        return false;
      }
      if (deltaY <= 0 || container.scrollTop > 0) {
        resetIndicator();
        return false;
      }

      const distance = Math.min(SHELL_MAX_PULL, deltaY * 0.46);
      const ready = distance >= SHELL_PULL_THRESHOLD;
      gesture.distance = distance;
      if (ready !== gesture.ready) {
        gesture.ready = ready;
        setVisualState(ready ? 'ready' : 'pulling');
        if (ready && typeof navigator.vibrate === 'function') navigator.vibrate(8);
      } else if (backdropRef.current?.dataset.state === 'idle') {
        setVisualState('pulling');
      }
      schedulePaint(distance);
      return true;
    };

    const finishGesture = async () => {
      if (!gesture.active || gesture.refreshing) return;
      if (!gesture.ready) {
        resetIndicator();
        return;
      }

      gesture.active = false;
      gesture.refreshing = true;
      setVisualState('refreshing');
      paintDistance(SHELL_REFRESH_DISTANCE);
      const startedAt = performance.now();

      try {
        await onRefresh();
      } finally {
        const remaining = Math.max(0, SHELL_REFRESH_MINIMUM_MS - (performance.now() - startedAt));
        if (remaining > 0) {
          await new Promise<void>((resolve) => window.setTimeout(resolve, remaining));
        }
        gesture.refreshing = false;
        resetIndicator();
      }
    };

    const onTouchStart = (event: TouchEvent) => {
      if (
        event.touches.length !== 1 ||
        !canStartGesture(event.target)
      ) return;
      beginGesture(event.touches[0].clientX, event.touches[0].clientY);
    };

    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      if (moveGesture(event.touches[0].clientX, event.touches[0].clientY) && event.cancelable) {
        event.preventDefault();
      }
    };

    const onTouchEnd = () => { void finishGesture(); };

    const onTouchCancel = () => {
      if (!gesture.refreshing) resetIndicator();
    };

    const onPointerDown = (event: PointerEvent) => {
      if (
        !enableMouseGesture ||
        event.pointerType !== 'mouse' ||
        event.button !== 0 ||
        !canStartGesture(event.target)
      ) return;
      gesture.pointerId = event.pointerId;
      beginGesture(event.clientX, event.clientY);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerId !== gesture.pointerId) return;
      if (moveGesture(event.clientX, event.clientY) && event.cancelable) event.preventDefault();
    };

    const onPointerEnd = (event: PointerEvent) => {
      if (event.pointerId !== gesture.pointerId) return;
      gesture.pointerId = null;
      void finishGesture();
    };

    const onPointerCancel = (event: PointerEvent) => {
      if (event.pointerId !== gesture.pointerId) return;
      gesture.pointerId = null;
      if (!gesture.refreshing) resetIndicator();
    };

    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd, { passive: true });
    container.addEventListener('touchcancel', onTouchCancel, { passive: true });
    container.addEventListener('pointerdown', onPointerDown);
    container.addEventListener('pointermove', onPointerMove);
    container.addEventListener('pointerup', onPointerEnd);
    container.addEventListener('pointercancel', onPointerCancel);
    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchCancel);
      container.removeEventListener('pointerdown', onPointerDown);
      container.removeEventListener('pointermove', onPointerMove);
      container.removeEventListener('pointerup', onPointerEnd);
      container.removeEventListener('pointercancel', onPointerCancel);
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
      snapCleanupRef.current?.();
      pullSurfaceRef.current?.style.removeProperty('--qs-pull-refresh-offset');
      if (pullSurfaceRef.current) delete pullSurfaceRef.current.dataset.pullRefreshState;
      pullSurfaceRef.current = null;
    };
  }, [disabled, enableMouseGesture, onRefresh, paintDistance, resetIndicator, scrollContainerRef, schedulePaint, setVisualState]);

  return (
    <div
      ref={backdropRef}
      data-slot="shell-pull-to-refresh"
      data-state="idle"
      role="status"
      aria-label="Pull to refresh"
      aria-live="polite"
      className="group pointer-events-none absolute inset-x-0 bottom-0 z-0 flex items-start justify-center bg-canvas text-foreground"
      style={{
        top: 'calc(var(--qs-shell-top-offset, 0px) + var(--qs-floating-header-band-compact, 64px))',
        opacity: 0,
        transition: 'opacity 120ms ease-out',
      }}
    >
      <div
        className="flex items-center gap-2.5 text-sm font-medium tracking-[0.01em]"
        style={{ paddingTop: '24px' }}
      >
        <span
          ref={iconRef}
          className="flex group-data-[state=ready]:animate-spin group-data-[state=refreshing]:animate-spin"
          style={{ transition: 'transform 100ms linear' }}
        >
          <RefreshCw aria-hidden="true" className="h-5 w-5" />
        </span>
        <span ref={statusRef}>Pull to refresh</span>
      </div>
    </div>
  );
}
